import { Switch } from "@/components/ui/switch";
import { SubBadge } from "@/components/admin/SectionShell";
import { HOWTO_STYLES, HowToByStyle } from "@/components/home/HowToGuide";
import { inputCls } from "./Fields";
import { HowToDownloads } from "./HowToDownloads";

const LOOKS = [
  ["banner", "Big banner under Register", "A dark green banner with a pulsing 'Watch the 3 steps' button and a live Register → Log in → Open Video strip. Impossible to miss."],
  ["button", "Small button beside Register", "A compact pulsing pill next to the Register button. Quiet and tidy."],
];

export const HowToPicker = ({ content, setC, authFetch }) => {
  const style = content.howto_style || "cards";
  const look = content.howto_look || "banner";
  const on = content.howto_enabled !== false;
  const pick = (k, v) => () => setC(k, v);
  return (
    <div className="mt-6 rounded-2xl border border-[#F2DFCE] bg-[#FDF6F0] p-5" data-testid="howto-picker">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <p className="font-medium"><SubBadge /> "How to open the video" guide (beside Register)</p>
        <div className="inline-flex items-center gap-2 text-sm" data-testid="howto-enabled-wrap">
          <span className={`text-xs font-medium ${on ? "text-[#128C4B]" : "text-muted-foreground"}`}>{on ? "Shown on website" : "Hidden"}</span>
          <Switch data-testid="howto-enabled" aria-label="Show the how-to guide on the website" checked={on} onCheckedChange={(v) => setC("howto_enabled", v)} />
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-4">An animated 3-step walkthrough (Register → Log in → Open Video). Choose how it appears on the website and which animation plays — the previews are live. Press <strong>Save content</strong> after choosing.</p>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Title / button text on the website</label>
          <input data-testid="content-howto_label" value={content.howto_label ?? ""} onChange={(e) => setC("howto_label", e.target.value)} className={inputCls} placeholder="How to open the video" />
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-white/70 px-4 py-3 mt-6">
          <span className="text-sm">Auto-play the animation<span className="block text-[11px] text-muted-foreground">Off = the viewer taps through the steps</span></span>
          <Switch data-testid="howto-autoplay" aria-label="Auto-play the animation" checked={content.howto_autoplay !== false} onCheckedChange={(v) => setC("howto_autoplay", v)} />
        </div>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Where it appears</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-5" data-testid="howto-look-options">
        {LOOKS.map(([v, name, desc]) => {
          const sel = look === v;
          return (
            <div role="button" tabIndex={0} key={v} data-testid={`howto-look-${v}`} aria-pressed={sel} onClick={pick("howto_look", v)} onKeyDown={(e) => e.key === "Enter" && setC("howto_look", v)} className={`cursor-pointer rounded-2xl border-2 bg-white px-4 py-3 transition-[border-color,box-shadow] ${sel ? "border-[#C86A51] shadow-[0_12px_30px_rgba(200,106,81,0.18)]" : "border-border hover:border-[#1A2E26]/30"}`}>
              <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold">{name}</p><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${sel ? "bg-[#C86A51] text-white" : "bg-muted text-muted-foreground"}`}>{sel ? "Selected" : "Choose"}</span></div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{desc}</p>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Animation inside the guide</p>
      <div className="grid lg:grid-cols-3 gap-3" data-testid="howto-style-options">
        {HOWTO_STYLES.map(([v, name, feel], idx) => {
          const sel = style === v;
          return (
            <div role="button" tabIndex={0} key={v} data-testid={`howto-style-${v}`} aria-pressed={sel} onClick={pick("howto_style", v)} onKeyDown={(e) => e.key === "Enter" && setC("howto_style", v)} className={`cursor-pointer text-left flex flex-col rounded-2xl border-2 bg-white p-3 transition-[border-color,box-shadow] ${sel ? "border-[#C86A51] shadow-[0_12px_30px_rgba(200,106,81,0.18)]" : "border-border hover:border-[#1A2E26]/30"}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-sm font-semibold flex items-center gap-2"><span className={`grid place-items-center w-5 h-5 rounded-full text-[10px] font-bold ${sel ? "bg-[#C86A51] text-white" : "bg-[#1A2E26]/10 text-[#1A2E26]"}`}>{idx + 1}</span>{name}</p>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${sel ? "bg-[#C86A51] text-white" : "bg-muted text-muted-foreground"}`}>{sel ? "Selected" : "Choose"}</span>
              </div>
              <div className="rounded-xl bg-[#F4F1EA] p-2 overflow-hidden pointer-events-none" style={{ minHeight: 120 }}>
                <div className={v === "phone" ? "scale-[0.5] origin-top-left h-[250px] w-[200%]" : ""}><HowToByStyle style={v} compact /></div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{feel}</p>
            </div>
          );
        })}
      </div>
      {authFetch && <HowToDownloads authFetch={authFetch} />}
    </div>
  );
};
